"use client"

import { useState, useMemo } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Copy, Trash2, ArrowRightLeft } from "lucide-react"
import { toast } from "sonner"

interface DiffLine {
  type: "added" | "removed" | "unchanged"
  content: string
  lineNumber: { left?: number; right?: number }
}

function computeDiff(original: string, modified: string, ignoreWhitespace: boolean): DiffLine[] {
  let left = original.split("\n")
  let right = modified.split("\n")

  if (ignoreWhitespace) {
    left = left.map((line) => line.trim())
    right = right.map((line) => line.trim())
  }

  // Simple LCS-based diff algorithm
  const m = left.length
  const n = right.length

  // Build LCS table
  const lcs: number[][] = Array(m + 1)
    .fill(null)
    .map(() => Array(n + 1).fill(0))

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (left[i - 1] === right[j - 1]) {
        lcs[i][j] = lcs[i - 1][j - 1] + 1
      } else {
        lcs[i][j] = Math.max(lcs[i - 1][j], lcs[i][j - 1])
      }
    }
  }

  // Backtrack to find diff
  let i = m
  let j = n
  const temp: DiffLine[] = []

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && left[i - 1] === right[j - 1]) {
      temp.push({
        type: "unchanged",
        content: ignoreWhitespace ? original.split("\n")[i - 1] : left[i - 1],
        lineNumber: { left: i, right: j },
      })
      i--
      j--
    } else if (j > 0 && (i === 0 || lcs[i][j - 1] >= lcs[i - 1][j])) {
      temp.push({
        type: "added",
        content: ignoreWhitespace ? modified.split("\n")[j - 1] : right[j - 1],
        lineNumber: { right: j },
      })
      j--
    } else if (i > 0) {
      temp.push({
        type: "removed",
        content: ignoreWhitespace ? original.split("\n")[i - 1] : left[i - 1],
        lineNumber: { left: i },
      })
      i--
    }
  }

  return temp.reverse()
}

interface DiffStats {
  additions: number
  deletions: number
  unchanged: number
}

function computeStats(diff: DiffLine[]): DiffStats {
  return diff.reduce(
    (acc, line) => {
      if (line.type === "added") acc.additions++
      else if (line.type === "removed") acc.deletions++
      else acc.unchanged++
      return acc
    },
    { additions: 0, deletions: 0, unchanged: 0 }
  )
}

export function TextDiff() {
  const [original, setOriginal] = useState("")
  const [modified, setModified] = useState("")
  const [ignoreWhitespace, setIgnoreWhitespace] = useState(false)
  const [showLineNumbers, setShowLineNumbers] = useState(true)

  const diff = useMemo(() => {
    if (!original && !modified) return []
    return computeDiff(original, modified, ignoreWhitespace)
  }, [original, modified, ignoreWhitespace])

  const stats = useMemo(() => computeStats(diff), [diff])

  const swapTexts = () => {
    const temp = original
    setOriginal(modified)
    setModified(temp)
  }

  const clearAll = () => {
    setOriginal("")
    setModified("")
  }

  const copyDiff = () => {
    const diffText = diff
      .map((line) => {
        const prefix = line.type === "added" ? "+ " : line.type === "removed" ? "- " : "  "
        return prefix + line.content
      })
      .join("\n")
    navigator.clipboard.writeText(diffText)
    toast.success("Diff copied to clipboard")
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Text Diff</CardTitle>
        <CardDescription>
          Compare two texts and see the differences highlighted. All processing happens in your browser.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Input Areas */}
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="original">Original Text</Label>
            <Textarea
              id="original"
              placeholder="Paste original text here..."
              value={original}
              onChange={(e) => setOriginal(e.target.value)}
              className="font-mono text-sm min-h-[150px] md:min-h-[200px]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="modified">Modified Text</Label>
            <Textarea
              id="modified"
              placeholder="Paste modified text here..."
              value={modified}
              onChange={(e) => setModified(e.target.value)}
              className="font-mono text-sm min-h-[150px] md:min-h-[200px]"
            />
          </div>
        </div>

        {/* Options and Actions */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center space-x-2">
              <Switch
                checked={ignoreWhitespace}
                onCheckedChange={setIgnoreWhitespace}
                id="ignore-whitespace"
              />
              <Label htmlFor="ignore-whitespace">Ignore whitespace</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                checked={showLineNumbers}
                onCheckedChange={setShowLineNumbers}
                id="show-line-numbers"
              />
              <Label htmlFor="show-line-numbers">Show line numbers</Label>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={swapTexts}>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Swap
            </Button>
            <Button variant="outline" size="sm" onClick={clearAll}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
            {diff.length > 0 && (
              <Button variant="outline" size="sm" onClick={copyDiff}>
                <Copy className="h-4 w-4 mr-2" />
                Copy Diff
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        {diff.length > 0 && (
          <div className="flex items-center gap-3">
            <Badge variant="outline" className="bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-300">
              +{stats.additions} additions
            </Badge>
            <Badge variant="outline" className="bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300">
              -{stats.deletions} deletions
            </Badge>
            <Badge variant="outline">
              {stats.unchanged} unchanged
            </Badge>
          </div>
        )}

        {/* Diff Output */}
        {diff.length > 0 && (
          <Tabs defaultValue="unified" className="w-full">
            <TabsList>
              <TabsTrigger value="unified">Unified</TabsTrigger>
              <TabsTrigger value="side-by-side">Side by Side</TabsTrigger>
            </TabsList>

            <TabsContent value="unified" className="mt-4">
              <div className="border rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <tbody className="font-mono text-sm">
                      {diff.map((line, index) => (
                        <tr
                          key={index}
                          className={
                            line.type === "added"
                              ? "bg-green-50 dark:bg-green-950/30"
                              : line.type === "removed"
                                ? "bg-red-50 dark:bg-red-950/30"
                                : ""
                          }
                        >
                          {showLineNumbers && (
                            <>
                              <td className="px-2 py-1 text-muted-foreground text-right w-12 select-none border-r">
                                {line.lineNumber.left || ""}
                              </td>
                              <td className="px-2 py-1 text-muted-foreground text-right w-12 select-none border-r">
                                {line.lineNumber.right || ""}
                              </td>
                            </>
                          )}
                          <td className="px-2 py-1 w-6 select-none">
                            <span
                              className={
                                line.type === "added"
                                  ? "text-green-600 dark:text-green-400"
                                  : line.type === "removed"
                                    ? "text-red-600 dark:text-red-400"
                                    : "text-muted-foreground"
                              }
                            >
                              {line.type === "added" ? "+" : line.type === "removed" ? "-" : " "}
                            </span>
                          </td>
                          <td className="px-2 py-1 whitespace-pre-wrap break-all">
                            {line.content || " "}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="side-by-side" className="mt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted px-3 py-2 border-b font-medium text-sm">Original</div>
                  <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                    <table className="w-full">
                      <tbody className="font-mono text-sm">
                        {diff
                          .filter((line) => line.type !== "added")
                          .map((line, index) => (
                            <tr
                              key={index}
                              className={line.type === "removed" ? "bg-red-50 dark:bg-red-950/30" : ""}
                            >
                              {showLineNumbers && (
                                <td className="px-2 py-1 text-muted-foreground text-right w-12 select-none border-r">
                                  {line.lineNumber.left || ""}
                                </td>
                              )}
                              <td className="px-2 py-1 whitespace-pre-wrap break-all">
                                {line.content || " "}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-muted px-3 py-2 border-b font-medium text-sm">Modified</div>
                  <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                    <table className="w-full">
                      <tbody className="font-mono text-sm">
                        {diff
                          .filter((line) => line.type !== "removed")
                          .map((line, index) => (
                            <tr
                              key={index}
                              className={line.type === "added" ? "bg-green-50 dark:bg-green-950/30" : ""}
                            >
                              {showLineNumbers && (
                                <td className="px-2 py-1 text-muted-foreground text-right w-12 select-none border-r">
                                  {line.lineNumber.right || ""}
                                </td>
                              )}
                              <td className="px-2 py-1 whitespace-pre-wrap break-all">
                                {line.content || " "}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        )}

        {!original && !modified && (
          <div className="text-center py-8 text-muted-foreground">
            <p>Enter text in both fields to compare</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
